/**
 * TIJD — Tijd & Klok rulebook.
 *
 * Sourced from DEFAULT_POLICY (Tijd entries) + MODULE_AUTHORING_GUIDE §4.
 *
 * v2 (mei 2026) — uitgebreid met hints voor DUUR / PLANNING / GEMIDDELDE /
 * SAMENGESTELD / AFSTAND / CONVERSIE / OMREKENEN / SCHEMA / TIJDZONES /
 * KALENDER, na cross-module drift-audit (15+ seed-numberlines die buiten
 * de oorspronkelijke hints vielen).
 */

import type { ModuleRulebook } from './types'

/**
 * Note on customTypeRoles below: the `clock` type is an example of a
 * fully visual/interactive custom question type. When we add the next
 * 100+ types, each one plugs in via a single entry like this — no
 * changes to defaultPolicy.ts or the pipeline.
 */

export const TIJD_RULEBOOK: ModuleRulebook = {
  moduleId: 'Tijd',
  shortCode: 'TIJD',
  displayName: 'Tijd',

  didactics: {
    principles: [
      'klok_lezen',
      'tijdsduur_berekenen',
      '12_24_uurs',
      'snelheid_formule',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'tijd',
    coreSummary:
      'Klok lezen, tijdsduur berekenen, kalenders, schemas en snelheidsberekeningen.',
  },

  notation: {
    forbiddenPatterns: [
      {
        pattern: '\\bkm/h\\b',
        label: 'Engelse snelheidsnotatie (gebruik km/u)',
      },
    ],
  },

  content: {
    distractorMisconceptionPattern:
      'eenheid|minuten|uren|seconden|klok|kalender|snelheid',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'uren en minuten',
      'tijdsduur',
      '24-uurs notatie',
      'v = s / t',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'clock',
      'digitalClock',
      'calendar',
      'timeline',
      'speedDiagram',
      'worldClockPanel',
      'rule',
    ],
    familyVisualHints: [
      // ── Klok-lezen ────────────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-ANALOOG',
        preferredStimulus: 'clock',
        autoExemptEmbedded: true,
        note: 'Analoge klok als stimulus voor klok-lees-families. autoExemptEmbedded dekt het clock-vraagtype: de renderer IS de klok-visual.',
      },
      {
        familyIdPattern: 'TIJD-DIGITAAL',
        preferredStimulus: 'digitalClock',
        autoExemptEmbedded: true,
        note: 'Digitale klok als stimulus. autoExemptEmbedded dekt clock / fill_in met eigen visueel oppervlak.',
      },
      {
        familyIdPattern: 'TIJD-12-24',
        preferredStimulus: 'digitalClock',
        preferredSupport: 'clock',
        autoExemptEmbedded: true,
        note: '12/24-uurs conversie: digitale weergave als primair, analoog als anker. autoExemptEmbedded dekt clock-vraagtype.',
      },

      // ── Kalender ──────────────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-KALENDER',
        preferredStimulus: 'calendar',
        autoExemptEmbedded: true,
        note: 'Kalender-visual voor kalender-vragen. autoExemptEmbedded dekt schedule_reading / step_solver met eigen visueel oppervlak.',
      },

      // ── Snelheid / Afstand ────────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-SNELHEID',
        preferredStimulus: 'speedDiagram',
        note: 'Snelheid-diagram met v=s/t formule.',
      },
      {
        familyIdPattern: 'TIJD-AFSTAND',
        preferredStimulus: 'speedDiagram',
        preferredSupport: 'speedDiagram',
        note: 'Afstand uit snelheid+tijd: speedDiagram als stimulus én support (showWhen: wrong) — sterker dan tekstuele formule-regel.',
      },

      // ── Tijdsduur / Planning — TIMELINE, niet numberline ──────────────
      {
        familyIdPattern: 'TIJD-DUUR',
        preferredStimulus: 'timeline',
        autoExemptEmbedded: true,
        note: 'Tijdsduur visualiseert als tijdlijn met start/eind — niet als generieke getallenlijn. autoExemptEmbedded dekt step_solver / clock met eigen visueel oppervlak.',
      },
      {
        familyIdPattern: 'TIJD-PLANNING',
        preferredStimulus: 'timeline',
        preferredSupport: 'rule',
        autoExemptEmbedded: true,
        note: 'Planning op tijdlijn — gebeurtenissen na elkaar zichtbaar. autoExemptEmbedded dekt schedule_reading / step_solver.',
      },
      {
        familyIdPattern: 'TIJD-SAMENGESTELD',
        preferredStimulus: 'timeline',
        autoExemptEmbedded: true,
        note: 'Samengestelde tijdsberekeningen op één tijdlijn. autoExemptEmbedded dekt step_solver met eigen visueel oppervlak.',
      },
      {
        familyIdPattern: 'TIJD-GEMIDDELDE',
        preferredStimulus: 'speedDiagram',
        preferredSupport: 'rule',
        note: 'Gemiddelde snelheid: speedDiagram (v=s/t) toont de driehoek-relatie; formule als support. Voor pure gemiddelde-tijden mag timeline.',
      },
      {
        familyIdPattern: 'TIJD-SCHEMA',
        preferredStimulus: 'timeline',
        autoExemptEmbedded: true,
        note: 'Schema/dienstregeling als tijdlijn. autoExemptEmbedded dekt schedule_reading-vraagtype: de renderer IS het schema.',
      },

      // ── Conversie / Omrekenen ─────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-CONVERSIE|TIJD-OMREKENEN',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tijdeenheden omrekenen (sec/min/uur/dag): regel-kaart met conversiefactoren als support.',
      },

      // ── Tijdzones / Wereldklok ────────────────────────────────────────
      {
        familyIdPattern: 'TIJD-TIJDZONES',
        preferredStimulus: 'worldClockPanel',
        note: 'Tijdzones met wereld-klokpanel.',
      },

      // ── Diagram (snelheid-tijd-grafiek e.d.) ──────────────────────────
      {
        familyIdPattern: 'TIJD-DIAGRAM',
        stimulusAllowed: ['lineChart', 'timeline', 'speedDiagram'],
        note: 'DIAGRAM is een catch-all familie: lineChart voor temperatuur/afstand-tijd, timeline voor gebeurtenissen, speedDiagram voor snelheid-tijd. Geen preferredSupport — kies per vraag.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'clock',
      'schedule_reading',
      'estimation_slider',
      'step_solver',
    ],
    customTypeRoles: [
      {
        type: 'clock',
        didacticRole: 'visual',
        preferredDok: 2,
        appropriateTiers: { min: 1, max: 12 },
        reason:
          'Interactieve klok dwingt directe representatie — sterker dan tekstuele MC.',
      },
      {
        type: 'schedule_reading',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Een schema interpreteren combineert tijdslezen + plannings-redenering.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^TIJD-(ANALOOG|DIGITAAL|12-24)',
        preferredTypes: ['clock', 'fill_in', 'multiple_choice'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Klok-aflezen: interactieve clock-renderer of exacte HH:MM-invoer — geen schatting of waar/onwaar.',
      },
      {
        // FIX (mei 2026): Patroon was '^TGL-TIJD-...' — legacy prefix uit pre-T8.
        // FamilyIds zijn genormaliseerd naar TIJD-* (april 2026). De oude
        // patronen matchten NIETS, waardoor type-hints nooit toegepast werden
        // en de governance-pipeline voor o.a. TIJD-AFSTAND op 0% bleef hangen.
        familyIdPattern: '^TIJD-(DUUR|OMREKENEN|CONVERSIE|SAMENGESTELD)',
        preferredTypes: ['fill_in', 'multiple_choice', 'step_solver'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Tijdsduur en omrekenen: exact aantal uren + minuten — geen slider, geen oordeel.',
      },
      {
        familyIdPattern: '^TIJD-(SCHEMA|PLANNING|KALENDER)',
        preferredTypes: ['schedule_reading', 'multiple_choice', 'fill_in'],
        discouragedTypes: ['estimation_slider'],
        note: 'Schema/planning/kalender: aflezen + redeneren — schedule_reading-type of MC, geen slider.',
      },
      {
        familyIdPattern: '^TIJD-(SNELHEID|AFSTAND)',
        preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'Snelheid/afstand: v=s/t exact uitrekenen — geen oordeel of schatting.',
      },
      {
        familyIdPattern: '^TIJD-(GEMIDDELDE|DIAGRAM)',
        preferredTypes: ['fill_in', 'multiple_choice'],
        discouragedTypes: ['true_false_series'],
        note: 'Gemiddelde tijdsduur of diagram-aflezen: exact resultaat uit data.',
      },
      {
        familyIdPattern: '^TIJD-TIJDZONES',
        preferredTypes: ['multiple_choice', 'fill_in'],
        discouragedTypes: ['estimation_slider'],
        note: 'Tijdzones: uurverschil + lokale tijd — exact, geen schatting.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'hours-minutes-swap',
      label: 'Uren/minuten verwisselen',
      description:
        '2:15 als "vijftien over twee" lezen i.p.v. de juiste analoge interpretatie.',
    },
    {
      id: '12-24-confusion',
      label: '12/24-uurs verwarring',
      description: "15:00 als 5 uur lezen i.p.v. 3 uur 's middags.",
    },
    {
      id: 'speed-formula-swap',
      label: 'Snelheidsformule verwisselen',
      description:
        's = v × t verwarren met v = s / t bij snelheid-vraagstukken.',
    },
  ],

  keyRules: [
    'Klok-visuals verplicht voor analoge tijdvragen',
    'Tijdsduur altijd in uren EN minuten vermelden',
    'Trap-distractor: uren/minuten verwisselen',
    'Snelheid: km/u (niet km/h), formule v=s/t',
    '12-uurs vs. 24-uurs onderscheid expliciet maken',
    'DUUR/PLANNING/SCHEMA → timeline (niet numberline) — tijd heeft eenheden, getallenlijn niet',
  ],

  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement:
      'TIJD explanation must show actual calculation with time units',
    distractorMisconceptionRequirement:
      'TIJD distractors should reference time unit/notation error',
  },

  authoringGuideAnchor: '#4-tijd--tijd--klok',
}
